import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { patientQueryKeys, patientsApi } from "../../../shared/api/patients";
import type {
  ConditionCreateRequest,
  ConditionResponse,
  ConditionSeverity,
} from "../../../shared/types/patient";
import { conditionSeverityLabel, formatDate } from "../../../shared/format";
import { apiError } from "../../../shared/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, X } from "lucide-react";

const severityBadgeVariant: Record<ConditionSeverity, BadgeProps["variant"]> = {
  MILD: "muted",
  MODERATE: "warning",
  SEVERE: "destructive",
} as Record<ConditionSeverity, BadgeProps["variant"]>;

export function ConditionsPanel({ patientId }: { patientId: string }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);

  const list = useQuery({
    queryKey: patientQueryKeys.conditions(patientId, false),
    queryFn: () => patientsApi.listConditions(patientId, false),
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => patientsApi.deleteCondition(patientId, id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: patientQueryKeys.conditions(patientId, false) }),
  });

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle className="text-base">Bệnh mãn tính</CardTitle>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setAdding((v) => !v)}
        >
          {adding ? (
            <>
              <X className="h-4 w-4" />
              Đóng
            </>
          ) : (
            <>
              <Plus className="h-4 w-4" />
              Thêm bệnh
            </>
          )}
        </Button>
      </CardHeader>

      <CardContent className="space-y-4">
        {adding && (
          <AddConditionForm
            patientId={patientId}
            onDone={() => {
              setAdding(false);
              qc.invalidateQueries({
                queryKey: patientQueryKeys.conditions(patientId, false),
              });
            }}
          />
        )}

        {list.isLoading && (
          <p className="text-sm text-muted-foreground">Đang tải…</p>
        )}
        {list.isError && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {apiError(list.error).detail}
          </div>
        )}
        {list.data && list.data.length === 0 && !adding && (
          <p className="text-sm text-muted-foreground">
            Chưa có bệnh mãn tính ghi nhận.
          </p>
        )}

        <div className="divide-y divide-border">
          {list.data?.map((c) => (
            <ConditionRow
              key={c.id}
              condition={c}
              onDelete={() => deactivate.mutate(c.id)}
              busy={deactivate.isPending}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ConditionRow({
  condition,
  onDelete,
  busy,
}: {
  condition: ConditionResponse;
  onDelete: () => void;
  busy: boolean;
}) {
  return (
    <div className="flex flex-col gap-3 py-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-foreground">{condition.nameVi}</span>
          {condition.icd10Code && (
            <span className="font-mono text-xs text-muted-foreground">
              {condition.icd10Code}
            </span>
          )}
          {condition.severity && (
            <Badge variant={severityBadgeVariant[condition.severity] ?? "muted"}>
              {conditionSeverityLabel[condition.severity]}
            </Badge>
          )}
        </div>
        <div className="text-sm text-muted-foreground">
          {condition.onsetDate && `Khởi phát ${formatDate(condition.onsetDate)}`}
          {condition.resolvedDate && ` · Khỏi ${formatDate(condition.resolvedDate)}`}
          {condition.notes && ` · ${condition.notes}`}
        </div>
      </div>
      <div className="shrink-0">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onDelete}
          disabled={busy}
        >
          Vô hiệu
        </Button>
      </div>
    </div>
  );
}

function AddConditionForm({
  patientId,
  onDone,
}: {
  patientId: string;
  onDone: () => void;
}) {
  const [data, setData] = useState<ConditionCreateRequest>({
    icd10Code: "",
    nameVi: "",
    onsetDate: null,
    severity: null,
    notes: "",
  });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (req: ConditionCreateRequest) =>
      patientsApi.createCondition(patientId, req),
    onSuccess: () => onDone(),
    onError: (e) => setError(apiError(e).detail),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!data.nameVi.trim()) {
          setError("Vui lòng nhập tên bệnh");
          return;
        }
        setError(null);
        create.mutate(data);
      }}
      className="space-y-4 rounded-md border bg-muted/50 p-4"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="cd-name">Tên bệnh (Tiếng Việt) *</Label>
          <Input
            id="cd-name"
            type="text"
            value={data.nameVi}
            onChange={(e) => setData((d) => ({ ...d, nameVi: e.target.value }))}
            placeholder="VD: Tăng huyết áp, Đái tháo đường típ 2"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cd-icd">Mã ICD-10</Label>
          <Input
            id="cd-icd"
            type="text"
            value={data.icd10Code ?? ""}
            onChange={(e) =>
              setData((d) => ({ ...d, icd10Code: e.target.value || null }))
            }
            placeholder="VD: I10"
            maxLength={8}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cd-sev">Mức độ</Label>
          <Select
            value={data.severity ?? "__none__"}
            onValueChange={(value) =>
              setData((d) => ({
                ...d,
                severity: (value === "__none__"
                  ? null
                  : value) as ConditionSeverity | null,
              }))
            }
          >
            <SelectTrigger id="cd-sev">
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">—</SelectItem>
              {(Object.keys(conditionSeverityLabel) as ConditionSeverity[]).map((s) => (
                <SelectItem key={s} value={s}>
                  {conditionSeverityLabel[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cd-onset">Khởi phát</Label>
          <Input
            id="cd-onset"
            type="date"
            value={data.onsetDate ?? ""}
            onChange={(e) =>
              setData((d) => ({ ...d, onsetDate: e.target.value || null }))
            }
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="cd-notes">Ghi chú</Label>
        <Input
          id="cd-notes"
          type="text"
          value={data.notes ?? ""}
          onChange={(e) => setData((d) => ({ ...d, notes: e.target.value || null }))}
        />
      </div>
      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onDone}>
          Hủy
        </Button>
        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? "Đang lưu…" : "Thêm"}
        </Button>
      </div>
    </form>
  );
}
